/**
 * GroupMetaPanel.tsx
 *
 * Column 1 of the group processing drawer: the standing context a technician
 * needs while working a step — who owns the group, which protocol it runs, and
 * what the workload is. Read-only; every action lives in the header or column 3.
 */

import React from "react";
import dayjs from "dayjs";

import {
	MetaDivider,
	MetaEmptyRow,
	MetaListItem,
	MetaPanel,
	MetaRow,
	MetaSection,
	StatusTag,
	useMetaPanelCollapse,
} from "../../../../../../components/shared";
import type { ProcessingGroup } from "../../../../../../types/lab/ProtocolTypes";
import type { TeamMember } from "../../../../../../types/admin/TeamTypes";
import { formatDuration, totalStepMinutes } from "./stepMeta";

const COLLAPSE_STORAGE_KEY = "mg-labgroup-collapsed";

interface GroupMetaPanelProps {
	group: ProcessingGroup;
	technician?: TeamMember;
	instanceCount: number;
	instanceLabel: "samples" | "analyses";
	completedSteps: number;
	totalSteps: number;
}

function formatDateTime(value: string | undefined | null): string {
	return value ? dayjs(value).format("D MMM YYYY, h:mma") : "—";
}

const GroupMetaPanel: React.FC<GroupMetaPanelProps> = ({
	group,
	technician,
	instanceCount,
	instanceLabel,
	completedSteps,
	totalSteps,
}) => {
	const [collapsed, toggle] = useMetaPanelCollapse(COLLAPSE_STORAGE_KEY);

	const protocol = group.protocol;
	const steps = protocol?.steps ?? [];

	const perInstanceMinutes = totalStepMinutes(steps);
	const methodCount = steps.reduce((sum, s) => sum + (s.method?.length ?? 0), 0);
	const materialCount = steps.reduce((sum, s) => sum + (s.materials?.length ?? 0), 0);
	const autoActionCount = steps.reduce(
		(sum, s) =>
			sum +
			(s.completion_actions?.length ?? 0) +
			(s.failure_actions?.length ?? 0) +
			(s.timeout_actions?.length ?? 0),
		0,
	);

	const technicianName = technician
		? `${technician.first_name ?? ""} ${technician.last_name ?? ""}`.trim() || "—"
		: group.assigned_to
		? `#${group.assigned_to}`
		: "Unassigned";

	const appliesTo = [...(protocol?.test_types ?? []), ...(protocol?.sample_types ?? [])];

	return (
		<MetaPanel>
			<MetaSection title="Group" open={!collapsed.group} onToggle={() => toggle("group")}>
				<MetaRow label="Status" value={<StatusTag status={group.status} size="small" />} />
				<MetaRow label="Technician" value={technicianName} />
				<MetaRow
					label={instanceLabel === "analyses" ? "Analyses" : "Samples"}
					value={<span style={{ fontVariantNumeric: "tabular-nums" }}>{instanceCount}</span>}
				/>
				<MetaRow
					label="Steps complete"
					value={
						<span style={{ fontVariantNumeric: "tabular-nums" }}>
							{totalSteps > 0 ? `${completedSteps} / ${totalSteps}` : "—"}
						</span>
					}
				/>
				<MetaRow label="Started" value={formatDateTime(group.started_at)} />
				{group.completed_at && <MetaRow label="Completed" value={formatDateTime(group.completed_at)} />}
			</MetaSection>

			<MetaDivider />

			<MetaSection title="Protocol" open={!collapsed.protocol} onToggle={() => toggle("protocol")}>
				<MetaRow label="Name" value={protocol?.name ?? "—"} />
				<MetaRow label="Version" value={protocol?.version ? `v${protocol.version}` : "—"} />
				{protocol?.description && <MetaRow label="Description" value={protocol.description} />}
				{appliesTo.length > 0 && (
					<MetaRow
						label="Applies to"
						value={
							<span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
								{appliesTo.map((type) => (
									<span
										key={type}
										style={{
											padding: "0 6px",
											borderRadius: "var(--mg-radius-xs)",
											border: "1px solid var(--mg-border)",
											background: "var(--mg-bg-subtle)",
											color: "var(--mg-text-secondary)",
											fontSize: 10,
											fontWeight: 500,
											lineHeight: "16px",
										}}
									>
										{type}
									</span>
								))}
							</span>
						}
					/>
				)}
			</MetaSection>

			<MetaDivider />

			<MetaSection title="Workload" open={!collapsed.workload} onToggle={() => toggle("workload")}>
				{totalSteps === 0 ? (
					<MetaEmptyRow>No steps defined</MetaEmptyRow>
				) : (
					<>
						<MetaListItem
							primary={`${totalSteps} step${totalSteps === 1 ? "" : "s"}`}
							secondary={[
								methodCount > 0 ? `${methodCount} methods` : "",
								materialCount > 0 ? `${materialCount} materials` : "",
								autoActionCount > 0 ? `${autoActionCount} auto actions` : "",
							]
								.filter(Boolean)
								.join(" · ")}
						/>
						<MetaRow
							label={`Estimated per ${instanceLabel === "analyses" ? "analysis" : "sample"}`}
							value={formatDuration(perInstanceMinutes)}
						/>
						<MetaRow
							label="Estimated for group"
							value={formatDuration(perInstanceMinutes * Math.max(instanceCount, 1))}
						/>
					</>
				)}
			</MetaSection>
		</MetaPanel>
	);
};

export default GroupMetaPanel;
